import { getCookie } from 'cookies-next'
import type { GetServerSidePropsContext, NextApiRequest, NextApiResponse } from 'next'

import { APP_ENV, OPERATIONS_SERVICE, PERSONALIZATION_SERVICE, getS2sEnv } from '@/config/env'
import { AUTH_COOKIE_NAME } from '@/server/routers/flows/utils/cookies'
import Api from '@api-service/api'
import type { CallOptions } from '@api-service/api/types'
import { getServiceUrlBases } from '@api-service/api/utils'
import type { Language } from '@constants/format'

const protocol = 'https://'
const apiDomain: string = APP_ENV !== 'production' ? 'api.gtsf.insure' : 'api.getsafe.eu'

export const apiBases = Object.freeze({
  user: `${protocol}user-service.${apiDomain}/api`,
  insurance: `${protocol}insurance-service.${apiDomain}/api`,
  payment: `${protocol}payment-service.${apiDomain}/api`,
  personalizationService: PERSONALIZATION_SERVICE,
  operationsService: OPERATIONS_SERVICE,
})

const API_PATH_PREFIX = '/api'

const withoutDuplicateApiPrefix = (api: Api): Api => {
  const call = api.call
  api.call = (callOptions: CallOptions) => {
    const base = callOptions.serviceName
      ? api.serviceUrlBases[callOptions.serviceName]?.url
      : callOptions.baseURL
    const isDuplicated =
      base?.endsWith(API_PATH_PREFIX) && callOptions.url.startsWith(`${API_PATH_PREFIX}/`)

    return call(
      isDuplicated
        ? { ...callOptions, url: callOptions.url.slice(API_PATH_PREFIX.length) }
        : callOptions
    )
  }
  return api
}

const createApiService = ({
  authorization,
  language,
}: {
  authorization: string
  language?: Language
}) =>
  withoutDuplicateApiPrefix(
    new Api({
      serviceUrlBases: getServiceUrlBases(apiBases),
      configGenerator: () => ({
        internalHeaders: {
          Authorization: authorization,
          'x-gtsf-content-language': language ?? '',
          'x-gtsf-api-client': 'web',
        },
      }),
    })
  )

export const getApiService = ({
  req,
  res,
  language,
}: {
  language?: Language
  req?: NextApiRequest | GetServerSidePropsContext['req']
  res?: NextApiResponse | GetServerSidePropsContext['res']
}) => {
  const authToken = getCookie(AUTH_COOKIE_NAME, { req, res })

  return createApiService({
    authorization: authToken ? `Token token="${authToken}"` : '',
    language,
  })
}

export const getS2sApiService = ({ language }: { language?: Language } = {}) => {
  const { S2S_OPERATIONS_SERVICE_AUTH_TOKEN } = getS2sEnv()

  return createApiService({
    authorization: `Token token="${S2S_OPERATIONS_SERVICE_AUTH_TOKEN}"`,
    language,
  })
}
